package hk.com.vr360_demo2.webapp.component.editor_rewrite.component

import emotion.react.css
import hk.com.vr360_demo2.webapp.component.SingleHotspotScenePanoramaPreview
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaLinkHotspot
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaSceneConfig
import mui.icons.material.ZoomIn
import mui.material.Button
import mui.material.Dialog
import mui.material.DialogActions
import mui.material.DialogContent
import mui.material.DialogTitle
import react.CSSProperties
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useMemo
import react.useState
import web.cssom.AlignItems
import web.cssom.Background
import web.cssom.BoxSizing
import web.cssom.Color
import web.cssom.Cursor
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.FontWeight
import web.cssom.JustifyContent
import web.cssom.Overflow
import web.cssom.Padding
import web.cssom.Position
import web.cssom.TextOverflow
import web.cssom.WhiteSpace
import web.cssom.number
import web.cssom.pct
import web.cssom.px
import web.html.HTMLElement
import kotlin.js.js
import kotlin.js.unsafeCast

external interface CreateHotspotDialogProps : Props {
    var open: Boolean
    var currentSceneId: String?
    var scenes: List<PanoramaSceneConfig>
    var dialogTitle: String?
    var initialTargetSceneId: String?
    var initialIconUrl: String?
    var onClose: () -> Unit
    var onConfirm: (targetSceneId: String, iconUrl: String) -> Unit
}

private val hotspotIconOptions: List<String> = (1..17).map { index -> "icon-${index}" }

val CreateHotspotDialog = FC<CreateHotspotDialogProps> { props ->
    val targetCandidates = useMemo(props.scenes, props.currentSceneId) {
        props.scenes.filter { it.id != props.currentSceneId }
    }
    val defaultTarget = targetCandidates.firstOrNull()?.id.orEmpty()
    val (selectedTargetId, setSelectedTargetId) = useState(defaultTarget)
    val (selectedIconName, setSelectedIconName) = useState(hotspotIconOptions.first())
    val (previewTargetSceneId, setPreviewTargetSceneId) = useState<String?>(null)
    val previewScene = previewTargetSceneId?.let { id ->
        targetCandidates.firstOrNull { it.id == id }
    }

    useEffect(props.open) {
        if (!props.open) {
            setPreviewTargetSceneId(null)
        }
    }

    useEffect(props.open, targetCandidates) {
        val current = selectedTargetId
        if (targetCandidates.isEmpty()) {
            if (current.isNotEmpty()) setSelectedTargetId("")
            return@useEffect
        }
        val exists = targetCandidates.any { it.id == current }
        if (!exists) {
            setSelectedTargetId(targetCandidates.first().id)
        }
    }

    useEffect(props.open, props.initialTargetSceneId, props.initialIconUrl) {
        if (!props.open) return@useEffect
        val initialTargetId = props.initialTargetSceneId
        if (!initialTargetId.isNullOrBlank() && targetCandidates.any { it.id == initialTargetId }) {
            setSelectedTargetId(initialTargetId)
        }
        val initialIconName = resolveHotspotIconName(props.initialIconUrl)
        setSelectedIconName(initialIconName)
    }

    Dialog {
        open = props.open
        this.fullWidth = true
        asDynamic().maxWidth = false
        asDynamic().PaperProps = js(
            """{
                sx: {
                    width: '960px',
                    maxWidth: '80vw',
                    minHeight: '360px',
                    maxHeight: '90vh'
                }
            }"""
        )
        onClose = { _, _ -> props.onClose() }

        DialogTitle {
            +(props.dialogTitle ?: "新增熱點")
        }

        DialogContent {
            div {
                css {
                    display = Display.grid
                    asDynamic().gridTemplateColumns = "1fr 1fr"
                    asDynamic().gap = "16px"
                    asDynamic()["@media (max-width: 960px)"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                        asDynamic().gridTemplateColumns = "1fr"
                    }
                }

                div {
                    css {
                        display = Display.flex
                        flexDirection = FlexDirection.column
                        asDynamic().gap = "8px"
                        minHeight = 380.px
                    }
                    span {
                        css { fontSize = 13.px }
                        +"第一步：選擇熱點圖標"
                    }
                    div {
                        css {
                            display = Display.grid
                            asDynamic().gridTemplateColumns = "repeat(6, 60px)"
                            asDynamic().justifyContent = "start"
                            asDynamic().gap = "8px"
                            maxHeight = 340.px
                            asDynamic().overflow = "auto"
//                            background = "#ff0000".unsafeCast<Background>()
                        }
                        hotspotIconOptions.forEach { iconName ->
                            val isActive = selectedIconName == iconName
                            button {
                                key = iconName.unsafeCast<Key>()
                                onClick = { setSelectedIconName(iconName) }
                                css {
                                    width = 60.px
                                    height = 60.px
                                    border = 0.px
                                    background = "rgb(82, 88, 114)".unsafeCast<Background>()
                                    display = Display.flex
                                    alignItems = AlignItems.center
                                    justifyContent = JustifyContent.center
                                    cursor = Cursor.pointer
                                    asDynamic().borderRadius = "6px"
                                    asDynamic().boxSizing = "border-box"
                                    asDynamic().border = if (isActive) "2px solid #f26522" else "2px solid #ffffff"
                                }
                                HotspotAnimatedImage {
                                    this.iconName = iconName
                                    width = 36
                                    height = 36
                                    backgroundColor = "rgb(82, 88, 114)"
                                    alt = "hotspot-icon"
                                    fps = 12
                                }
                            }
                        }
                    }
                }

                div {
                    css {
                        display = Display.flex
                        flexDirection = FlexDirection.column
                        asDynamic().gap = "8px"
                    }
                    span {
                        css { fontSize = 13.px }
                        +"第二步：選擇要去的場景"
                    }
                    div {
                        css {
                            display = Display.grid
                            asDynamic().gridTemplateColumns = "repeat(4, 90px)"
                            asDynamic().justifyContent = "start"
                            asDynamic().gap = "10px"
                            maxHeight = 300.px
                            asDynamic().overflow = "auto"
                        }

                        targetCandidates.forEach { scene ->
                            val isActive = selectedTargetId == scene.id
                            div {
                                key = scene.id.unsafeCast<Key>()
                                onClick = { setSelectedTargetId(scene.id) }
                                css {
                                    border = 0.px
                                    background = "rgba(255,255,255,0)".unsafeCast<Background>()
                                    width = 90.px
                                    padding = 0.px
                                    display = Display.flex
                                    flexDirection = FlexDirection.column
                                    alignItems = AlignItems.center
                                    asDynamic().gap = "6px"
                                    cursor = Cursor.pointer
                                    asDynamic().borderRadius = "6px"
                                    asDynamic().boxSizing = "border-box"
                                }
                                div {
                                    css {
                                        position = Position.relative
                                        width = 90.px
                                        height = 90.px
                                    }
                                    img {
                                        src = scene.cover ?: "/assets/images/default-avatar.svg"
                                        alt = scene.name ?: scene.id
                                        css {
                                            width = 90.px
                                            height = 90.px
                                            asDynamic().objectFit = "cover"
                                            asDynamic().borderRadius = "4px"
                                            asDynamic().boxSizing = "border-box"
                                            asDynamic().border =
                                                if (isActive) "2px solid #f26522" else "2px solid #ffffff"
                                        }
                                        onError = {
                                            val target = it.target as? HTMLElement
                                            target?.setAttribute("src", "/assets/images/default-avatar.svg")
                                        }
                                    }
                                    button {
                                        title = "預覽全景"
                                        onClick = { event ->
                                            event.stopPropagation()
                                            setPreviewTargetSceneId(scene.id)
                                        }
                                        css {
                                            position = Position.absolute
                                            right = 2.px
                                            bottom = 2.px
                                            width = 22.px
                                            height = 22.px
                                            border = 0.px
                                            padding = Padding(0.px, 0.px)
                                            display = Display.flex
                                            alignItems = AlignItems.center
                                            justifyContent = JustifyContent.center
                                            cursor = Cursor.pointer
                                            asDynamic().borderRadius = "4px"
                                            asDynamic().boxSizing = "border-box"
                                            asDynamic().zIndex = 1
                                            background = "rgba(0,0,0,0.45)".unsafeCast<Background>()
                                            color = Color("#ffffff")
                                            asDynamic()["& .MuiSvgIcon-root"] =
                                                js("{}").unsafeCast<CSSProperties>().apply {
                                                    asDynamic().width = "14px"
                                                    asDynamic().height = "14px"
                                                }
                                        }
                                        ZoomIn {}
                                    }
                                }
                                span {
                                    css {
                                        fontSize = 11.px
                                        asDynamic().maxWidth = "90px"
                                        whiteSpace = WhiteSpace.nowrap
                                        overflow = Overflow.hidden
                                        textOverflow = TextOverflow.ellipsis
                                    }
                                    +(scene.name ?: scene.id)
                                }
                            }
                        }
                    }
                }
            }
        }

        DialogActions {
            asDynamic().sx = js(
                """{
                    justifyContent: 'center'
                }"""
            )
            Button {
                onClick = { props.onClose() }
                variant = mui.material.ButtonVariant.outlined
                asDynamic().sx = js(
                    """{
                        minWidth: '80px',
                        width: '80px',
                        minHeight: '28px',
                        height: '28px',
                        padding: '0',
                        fontSize: '13px',
                        lineHeight: 1,
                        borderRadius: '4px',
                        color: '#f26522',
                        borderColor: '#f26522',
                        backgroundColor: '#ffffff',
                        '&:hover': {
                            borderColor: '#f26522',
                            backgroundColor: 'rgba(242,101,34,0.08)'
                        }
                    }"""
                )
                +"取消"
            }
            Button {
                disabled = selectedTargetId.isBlank()
                variant = mui.material.ButtonVariant.contained
                asDynamic().sx = js(
                    """{
                        minWidth: '80px',
                        width: '80px',
                        minHeight: '28px',
                        height: '28px',
                        padding: '0',
                        fontSize: '13px',
                        lineHeight: 1,
                        borderRadius: '4px',
                        backgroundColor: '#f26522',
                        color: '#ffffff',
                        '&:hover': {
                            backgroundColor: '#e45a1b'
                        }
                    }"""
                )
                onClick = {
                    props.onConfirm(selectedTargetId, hotspotIconNameToUrl(selectedIconName))
                }
                +"確定"
            }
        }
    }

    Dialog {
        open = previewScene != null
        onClose = { _, _ -> setPreviewTargetSceneId(null) }
        this.fullWidth = true
        asDynamic().maxWidth = "md"
        asDynamic().PaperProps = js(
            """{
                sx: {
                    width: '720px',
                    maxWidth: '92vw',
                    maxHeight: '85vh'
                }
            }"""
        )
        DialogTitle {
            div {
                css {
                    display = Display.flex
                    alignItems = AlignItems.center
                    justifyContent = JustifyContent.spaceBetween
                    width = 100.pct
                    asDynamic().gap = "8px"
                    boxSizing = BoxSizing.borderBox
                }
                span {
                    css {
                        flexGrow = number(1.0)
                        minWidth = 0.px
                        overflow = Overflow.hidden
                        textOverflow = TextOverflow.ellipsis
                        whiteSpace = WhiteSpace.nowrap
                        fontSize = 20.px
                        fontWeight = FontWeight.bold
                    }
                    +(previewScene?.let { sc -> sc.name ?: sc.id } ?: "場景預覽")
                }
                button {
                    title = "關閉"
                    onClick = { setPreviewTargetSceneId(null) }
                    css {
                        background = "none".unsafeCast<Background>()
                        border = 0.px
                        cursor = Cursor.pointer
                        padding = Padding(4.px, 4.px)
                        flexShrink = number(0.0)
                        display = Display.flex
                        alignItems = AlignItems.center
                        justifyContent = JustifyContent.center
                    }
                    img {
                        src = "/assets/images/close.svg"
                        alt = "關閉"
                        css {
                            width = 24.px
                            height = 24.px
                            display = Display.block
                        }
                    }
                }
            }
        }
        DialogContent {
            div {
                css {
                    width = 100.pct
                    height = 420.px
                }
                previewScene?.let { sc ->
                    SingleHotspotScenePanoramaPreview {
                        scene = sc
                        linkHotspot = PanoramaLinkHotspot(
                            yaw = 0.0,
                            pitch = 0.0,
                            target = sc.id,
                            rotation = null,
                            imageName = hotspotIconNameToUrl(selectedIconName),
                        )
                        autorotateEnabled = false
                    }
                }
            }
        }
    }
}
